import { useState, useEffect, useId } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Checkbox } from "@/shared/ui/checkbox";
import type {
  ClassificationRule,
  UpdateClassificationRuleRequest,
} from "@/services/api/classification-rules";
import { KeywordsInput, WeightInput } from "./components";
import { useClassificationRuleForm } from "./hooks/useClassificationRuleForm";

interface ClassificationRuleEditDialogProps {
  open: boolean;
  rule: ClassificationRule | null;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    id: string | number,
    data: UpdateClassificationRuleRequest
  ) => void;
}

export function ClassificationRuleEditDialog({
  open,
  rule,
  isLoading,
  onOpenChange,
  onSubmit,
}: ClassificationRuleEditDialogProps) {
  const [value, setValue] = useState("");
  const [isActive, setIsActive] = useState(true);

  const form = useClassificationRuleForm();
  const descriptionId = useId();

  useEffect(() => {
    if (rule && open) {
      setValue(rule.value || "");
      form.setWeight(rule.weight ?? 0);
      setIsActive(rule.isActive);
      form.setKeywords(rule.keywords || []);
      form.setWeightError(null);
      form.setKeywordsError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.id, open]);

  if (!rule) return null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.validate()) return;

    onSubmit(rule.id, {
      value: value || "",
      weight: form.getWeightValue(),
      isActive,
      keywords: form.keywords,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle>Edit classification rule</DialogTitle>
          <DialogDescription>
            Update the classification rule information below. You can modify the
            value, weight, active status, and manage keywords.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-ink-800">Value</label>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter value"
                required
              />
            </div>
            <WeightInput
              value={form.weight}
              error={form.weightError}
              onChange={form.setWeight}
              onErrorChange={form.setWeightError}
            />
          </div>

          <KeywordsInput
            keywords={form.keywords}
            error={form.keywordsError}
            onKeywordsChange={form.setKeywords}
            disabled={isLoading}
          />

          <div className="flex items-center gap-2">
            <Checkbox
              id="rule-active"
              checked={isActive}
              onCheckedChange={(checked) => setIsActive(checked === true)}
            />
            <label
              htmlFor="rule-active"
              className="text-sm font-medium text-ink-800 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Active
            </label>
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
